/**
 * useTheme — manages dark/light theme with localStorage persistence
 */
import { useState, useEffect } from 'react';

const STORAGE_KEY = 'thrive-theme';
const DARK_PROMPT_KEY = 'thrive-dark-mode-prompted';

const getInitialTheme = () => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'dark' || stored === 'light') return stored;
  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
};

// Compute once at module load: should we show the dark mode prompt?
const shouldPrompt = (() => {
  const stored = localStorage.getItem(STORAGE_KEY);
  const prompted = localStorage.getItem(DARK_PROMPT_KEY);
  if (prompted || stored) return false;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches || false;
})();

const useTheme = () => {
  const [theme, setTheme] = useState(getInitialTheme);
  const [showDarkPrompt, setShowDarkPrompt] = useState(shouldPrompt);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const dismissDarkPrompt = () => {
    setShowDarkPrompt(false);
    localStorage.setItem(DARK_PROMPT_KEY, 'true');
  };

  const switchToLight = () => {
    setTheme('light');
    dismissDarkPrompt();
  };

  const toggleTheme = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));

  return { theme, toggleTheme, isDark: theme === 'dark', showDarkPrompt, dismissDarkPrompt, switchToLight };
};

export default useTheme;
